import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert, FileCheck2, Mail, BadgeCheck } from "lucide-react";

const fmtDate = (ts) => { try { return new Date(ts).toLocaleDateString("tr-TR"); } catch { return ""; } };

/**
 * VKN/TCKN'yi backend'de ALGORİTMİK doğrular.
 *  - mode="portal" (varsayılan): ayrıca e-Fatura/e-Arşiv portalını + (varsa) portal e-postasını getirir.
 *  - mode="validate": yalnız kimlik geçerliliğini doğrular (firma/çalışan kartı için).
 * Doğrulama başarılı olunca onVerified(ISO) çağrılır; verifiedAt prop'u ile "Doğrulandı · tarih" rozeti gösterilir.
 */
export default function TaxPortalLookup({ taxNo, type, portal, docType, onSelect, onEmail, onVerified, verifiedAt, name, mode = "portal", testid = "tax-portal" }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const validate = mode === "validate";

  const lookup = async () => {
    const id = (taxNo || "").trim();
    if (!id) return toast.error("Önce Vergi No / TC Kimlik No girin");
    setLoading(true);
    try {
      const { data } = await api.post("/tax/portal-lookup", { identifier: id, type, name: name || "" });
      setResult(data);
      if (!data.valid) { toast.error(data.message); return; }
      onVerified?.(new Date().toISOString());
      if (validate) {
        toast.success(type === "individual" ? "TC Kimlik No geçerli ✓" : "Vergi No geçerli ✓");
      } else {
        toast.success(data.message);
        const first = data.portals?.[0];
        onSelect?.({ e_document_type: data.e_document_type, e_invoice_portal: first?.id || "" });
        if (onEmail && data.emails?.length === 1) { onEmail(data.emails[0]); toast.success(`Portal e-postası dolduruldu: ${data.emails[0]}`); }
        else if ((data.emails?.length || 0) > 1) toast.info(`${data.emails.length} portal e-postası bulundu — listeden seçin`);
      }
    } catch (e) {
      toast.error("Sorgulanamadı");
    } finally { setLoading(false); }
  };

  return (
    <div className="col-span-2 rounded-md bg-slate-50 p-2.5 ring-1 ring-inset ring-slate-200" data-testid={`${testid}-box`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold text-slate-600">{validate ? "Kimlik Doğrulama" : "E-Fatura / E-Arşiv Portalı"}</span>
        <Button type="button" data-testid={`${testid}-lookup`} onClick={lookup} disabled={loading} size="sm" variant="outline" className="h-7 border-slate-300 text-[11px]">
          {loading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <FileCheck2 className="mr-1 h-3 w-3" />} {validate ? "Doğrula" : "Doğrula & Portal Getir"}
        </Button>
      </div>

      {verifiedAt && (
        <p className="mt-1.5 inline-flex items-center gap-1 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200" data-testid={`${testid}-verified`}>
          <BadgeCheck className="h-3 w-3" /> Doğrulandı · {fmtDate(verifiedAt)}
        </p>
      )}

      {result?.valid && result?.taxpayer_title && (
        <p className="mt-1 text-[10px] text-slate-500" data-testid={`${testid}-title-match`}>
          Portal ünvanı: <b>{result.taxpayer_title}</b>
          {result.title_match === true && <span className="ml-1 text-emerald-600">· ünvan eşleşti ✓ (yaklaşık)</span>}
          {result.title_match === false && <span className="ml-1 text-amber-600">· ünvan farklı görünüyor (yine de kaydedilebilir)</span>}
        </p>
      )}

      {result && (result.valid ? (
        <div className="mt-2 space-y-1.5" data-testid={`${testid}-result`}>
          <p className="flex items-center gap-1 text-[11px] font-medium text-emerald-600">
            <ShieldCheck className="h-3.5 w-3.5" /> {validate ? (type === "individual" ? "TC Kimlik No geçerli ✓" : "Vergi No geçerli ✓") : result.message}
          </p>
          {!validate && (
            <>
              <div>
                <label className="text-[10px] font-semibold uppercase text-slate-400">Portal {result.multiple ? "(birden fazla — seçin)" : ""}</label>
                <select data-testid={`${testid}-select`} value={portal || ""}
                  onChange={(e) => onSelect?.({ e_document_type: docType || result.e_document_type, e_invoice_portal: e.target.value })}
                  className="mt-0.5 h-9 w-full rounded-md border border-input bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green">
                  {result.portals.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.note}</option>)}
                </select>
              </div>
              {(result.emails?.length || 0) > 0 && (
                <div data-testid={`${testid}-emails`}>
                  <label className="text-[10px] font-semibold uppercase text-slate-400">Portal E-postası {result.email_multiple ? "(birden fazla — seçin)" : ""}</label>
                  {result.email_multiple ? (
                    <select data-testid={`${testid}-email-select`} defaultValue="" onChange={(e) => e.target.value && onEmail?.(e.target.value)}
                      className="mt-0.5 h-9 w-full rounded-md border border-input bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green">
                      <option value="">Seçin…</option>
                      {result.emails.map((em) => <option key={em} value={em}>{em}</option>)}
                    </select>
                  ) : (
                    <p className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-slate-700"><Mail className="h-3 w-3 text-brand-green" /> {result.emails[0]}</p>
                  )}
                  {result.email_source && <p className="text-[10px] text-slate-400">Kaynak: {String(result.email_source).toUpperCase()} entegratörü</p>}
                </div>
              )}
              {type === "corporate" && (result.emails?.length || 0) === 0 && (
                <p className="text-[10px] text-slate-400" data-testid={`${testid}-no-email`}>Aktif entegratör mükellef e-postası döndürmedi (canlı e-Fatura sözleşmesi gerekir).</p>
              )}
              <p className="text-[10px] text-slate-400">Belge türü: <b>{result.e_document_type === "e_fatura" ? "e-Fatura" : "e-Arşiv"}</b></p>
            </>
          )}
        </div>
      ) : (
        <p className="mt-2 flex items-center gap-1 text-[11px] font-medium text-red-600" data-testid={`${testid}-invalid`}><ShieldAlert className="h-3.5 w-3.5" /> {result.message}</p>
      ))}
    </div>
  );
}
